/**
 * COPY STRATEGY — AccountantVsFinhudasPage
 *
 * Awareness:      Product-Aware (Stage 4/5)
 *                 Reader already pays an accountant, being shown Finhudas as
 *                 cheaper/better alternative. Knows the problem and current solution.
 *
 * Sophistication: Stage 3 — Mechanism
 *                 New mechanism: DIY automation vs human accountant.
 *                 "Your accountant does copy-paste for 40€ — you can do it in 3 min."
 *
 * Avatar:         Active driver paying accountant €30–50/month.
 *                 Earning €1000–3000/month. Questions accountant's value but
 *                 hasn't switched yet due to inertia or fear of doing it wrong.
 *
 * Hook:           Agitation — "ką tiksliai jis daro?" (what exactly does he do?)
 * Structure:      Question value → Expose simplicity → Compare cost → CTA
 * Proof:          Step-by-step showing accountant's work is simple copy-paste
 * Funnel:         Commission VAT, 30% norm, driver taxes articles
 */
import { motion } from 'framer-motion'
import { PiCheckCircle, PiCurrencyEur, PiRobot } from 'react-icons/pi'

import HeaderBack from '@/components/dashboard/Header/HeaderBack'
import MotionText from '@/components/common/Motion/MotionText'
import MotionDiv from '@/components/common/Motion/MotionDiv'
import ButtonLink from '@components/common/Button/ButtonLink'
import FinhudasCTA from '@/components/common/Blog/FinhudasCTA'
import RelatedArticles from '@/components/common/Blog/RelatedArticles'
import { usePageSetup } from '@/hooks/usePageSetup'

const AccountantVsFinhudasPage = () => {
  usePageSetup('Kodėl mokėti buhalteriui 40€ už Bolt deklaracijas yra pinigų deginimas', {
    scroll: true,
    description: 'Ką tiksliai daro jūsų buhalteris už 40€/mėn.? Rankinį copy-paste darbą. Per metus tai €480. Palyginimas žingsnis po žingsnio: buhalteris vs Finhudas (7€/mėn.).',
    type: 'article',
    datePublished: '2026-02-03',
  })

  return (
    <>
      <HeaderBack to="/blog" />
      <div className="min-h-full bg-white px-5 pb-8 flex flex-col">

        <MotionText as="h1" className="text-zinc-900 text-2xl font-medium mt-3 mb-2">
          Kodėl mokėti buhalteriui 40€ už Bolt deklaracijas yra pinigų deginimas
        </MotionText>

        <MotionDiv className="flex items-center gap-2 mb-6">
          <span className="text-xs font-medium text-zinc-500">Apskaita</span>
          <span className="text-xs text-zinc-300">•</span>
          <span className="text-xs text-zinc-500">6 min skaitymo</span>
        </MotionDiv>

        <MotionText as="p" className="text-zinc-700 text-base mb-4">
          Kiek mokate buhalteriui per mėnesį? 30€? 50€? O ką tiksliai jis daro?
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-2">
          Jei vairuojate „Bolt" ar „Uber", jūsų apskaita nėra sudėtinga. Tai ta pati formulė kiekvieną mėnesį:
        </MotionText>

        <MotionDiv className="space-y-2 mb-4">
          <MotionText as="p" className="text-zinc-700 text-sm">
            1. Atsisiųsti Bolt ataskaitą (PDF)
          </MotionText>
          <MotionText as="p" className="text-zinc-700 text-sm">
            2. Perrašyti skaičius į Excel ar popierių
          </MotionText>
          <MotionText as="p" className="text-zinc-700 text-sm">
            3. Apskaičiuoti Atvirkštinį PVM nuo komisinių
          </MotionText>
          <MotionText as="p" className="text-zinc-700 text-sm">
            4. Užpildyti VMI deklaracijas
          </MotionText>
          <MotionText as="p" className="text-zinc-700 text-sm">
            5. Sumokėti mokesčius
          </MotionText>
        </MotionDiv>

        <MotionText as="p" className="text-zinc-700 text-base font-medium mb-2">
          Ar tai tikrai verta 40€ per mėnesį?
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-2">
          Būkime atviri — dauguma buhalterių, dirbančių su pavežėjais, daro būtent tai: rankinį duomenų suvedimą. Jie parsisiunčia jūsų Bolt ataskaitą, perrašo skaičius ir užpildo formas. Už tai jūs mokate 30–100€ kiekvieną mėnesį.
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base">
          Per metus tai sudaro <strong>360–1 200€</strong>. Vien už copy-paste darbą.
        </MotionText>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Ką tiksliai daro buhalteris vs. Finhudas?
        </MotionText>

        <MotionDiv className="border border-zinc-250 rounded-xl overflow-hidden mb-4">
          <div className="grid grid-cols-3 bg-zinc-50 p-3 border-b border-zinc-250">
            <MotionText as="p" className="text-zinc-500 text-xs font-medium">Žingsnis</MotionText>
            <MotionText as="p" className="text-zinc-500 text-xs font-medium">Buhalteris</MotionText>
            <MotionText as="p" className="text-zinc-500 text-xs font-medium">Finhudas</MotionText>
          </div>

          {[
            { step: 'Bolt PDF', accountant: 'Siunčiate el. paštu', finhudas: 'Įkeliate tiesiogiai' },
            { step: 'Pajamų nuskaitymas', accountant: 'Rankinis kopijavimas', finhudas: 'Automatinis OCR' },
            { step: 'Atvirkštinis PVM', accountant: 'Rankinė formulė', finhudas: 'Automatinis (21%)' },
            { step: '30% vs faktinės', accountant: 'Dauguma neskaičiuoja', finhudas: 'Vienu paspaudimu' },
            { step: 'Mokesčių skaičiavimas', accountant: 'Excel lentelė', finhudas: 'GPM/VSD/PSD realiu laiku' },
            { step: 'Mėnesinis mokestis', accountant: '30–100€', finhudas: '7€' },
          ].map((row, i) => (
            <div key={i} className={`grid grid-cols-3 p-3 ${i % 2 === 0 ? 'bg-white' : 'bg-zinc-50'} ${i < 5 ? 'border-b border-zinc-100' : ''}`}>
              <MotionText as="p" className="text-zinc-900 text-xs font-medium">{row.step}</MotionText>
              <MotionText as="p" className="text-zinc-600 text-xs">{row.accountant}</MotionText>
              <MotionText as="p" className="text-blue-600 text-xs font-medium">{row.finhudas}</MotionText>
            </div>
          ))}
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Kaip veikia Finhudas?
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-4">
          Finhudas sukurtas būtent Lietuvos pavežėjams. Viskas užtrunka 3 minutes:
        </MotionText>

        <MotionDiv className="bg-zinc-100 rounded-2xl overflow-hidden mb-4">
          <div className="p-5 space-y-5">
            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  1
                </div>
                <div className="flex-1 w-px bg-zinc-300 my-1.5" />
              </div>
              <div className="pb-1">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-0.5">
                  Įkelkite Bolt ataskaitą
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  Parsisiųskite PDF iš partners.bolt.eu → įkelkite į Finhudas. Sistema automatiškai nuskaito pajamas ir kelionių duomenis.
                </MotionText>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  2
                </div>
                <div className="flex-1 w-px bg-zinc-300 my-1.5" />
              </div>
              <div className="pb-1">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-0.5">
                  Komisinių PVM — automatiškai
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  Įkelkite Bolt komisinių sąskaitą faktūrą. Finhudas apskaičiuoja Atvirkštinį PVM (21%) ir paruošia duomenis deklaracijai.
                </MotionText>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  3
                </div>
              </div>
              <div>
                <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-0.5">
                  30% vs faktinės — vienu paspaudimu
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  Sistema palygina, kuris atskaitymų metodas taupo daugiau. Matote tikslią sutaupymą eurais.
                </MotionText>
              </div>
            </div>
          </div>
        </MotionDiv>

        <MotionDiv className="py-4 bg-zinc-50 rounded-xl mb-4">
          <MotionText as="h3" className="text-zinc-900 text-base font-medium mb-2">
            Mokesčiai pagal 2026 m. tarifus
          </MotionText>
          <MotionText as="p" className="text-zinc-700 text-sm mb-2">
            Finhudas skaičiuoja GPM (20% su kredito sistema), VSD (12,52%), PSD (6,98%) — viskas pagal naujausius Lietuvos tarifus. Mokesčių kalendorius rodo, kiek ir kada mokėti.
          </MotionText>
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Kiek sutaupote per metus?
        </MotionText>

        <MotionDiv className="p-4 bg-white border border-zinc-250 rounded-xl mb-4">
          <MotionDiv className="border-t border-zinc-250 pt-3 mt-3">
            <MotionText as="p" className="text-zinc-700 text-sm font-medium mb-2">
              Skaičiavimas:
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Buhalteris: 40€ × 12 mėn. = 480€/metus
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Finhudas: 7€ × 12 mėn. = 84€/metus
            </MotionText>
            <MotionText as="p" className="text-green-700 text-sm font-mono border-t border-zinc-250 pt-2 mt-2 font-medium">
              Sutaupymas: 396€ per metus
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionDiv className="p-4 bg-green-50 border border-green-200 rounded-xl mb-4">
          <div className="flex gap-3">
            <PiCheckCircle className="min-w-6 h-6 text-green-700" />
            <div>
              <MotionText as="h3" className="text-green-900 text-sm font-medium mb-2">
                Plius: ko buhalteris nedaro
              </MotionText>
              <MotionText as="p" className="text-green-800 text-sm">
                Dauguma buhalterių nelygina 30% normos su faktinėmis išlaidomis. Finhudas tai daro automatiškai — ir galite sutaupyti papildomus 100–300€ per metus pasirinkdami teisingą metodą.
              </MotionText>
            </div>
          </div>
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Kam tinka buhalteris, o kam — Finhudas?
        </MotionText>

        <MotionDiv className="bg-zinc-100 rounded-2xl overflow-hidden mb-4">
          <div className="p-4">
            <div className="flex gap-3 mb-2">
              <PiRobot className="min-w-5 h-5 text-blue-600" />
              <MotionText as="h3" className="text-zinc-900 text-sm font-medium">
                Finhudas tinka, jei:
              </MotionText>
            </div>
            <ul className="space-y-1.5">
              <li className="text-zinc-700 text-sm">✓ Vairuojate „Bolt“ ir/arba „Uber“</li>
              <li className="text-zinc-700 text-sm">✓ Dirbate pagal individualią veiklą</li>
              <li className="text-zinc-700 text-sm">✓ Norite matyti savo tikrus skaičius realiu laiku</li>
              <li className="text-zinc-700 text-sm">✓ Norite sutaupyti 300–1 000€ per metus</li>
            </ul>
          </div>

          <div className="border-t border-zinc-200" />

          <div className="p-4">
            <div className="flex gap-3 mb-2">
              <PiCurrencyEur className="min-w-5 h-5 text-zinc-600" />
              <MotionText as="h3" className="text-zinc-900 text-sm font-medium">
                Buhalteris tinka, jei:
              </MotionText>
            </div>
            <ul className="space-y-1.5">
              <li className="text-zinc-700 text-sm">✓ Turite kelias veiklas (ne tik pavežėjimą)</li>
              <li className="text-zinc-700 text-sm">✓ Reikia sudėtingų konsultacijų</li>
              <li className="text-zinc-700 text-sm">✓ Viršijate PVM ribą ir reikia pilnos PVM apskaitos</li>
            </ul>
          </div>
        </MotionDiv>

        <MotionText as="p" className="text-zinc-700 text-base mt-6 mb-2">
          Profesionalūs pavežėjai, kurie uždirba 1 000–3 000€ per mėnesį, seniai suprato: kam mokėti duoklę už tai, ką gali padaryti pats per 3 minutes?
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-6">
          Pabandykite nemokamai. Jei jūsų buhalteris daro kažką, ko Finhudas negali — grįžkite pas jį. Bet pirmiausia pažiūrėkite skaičius.
        </MotionText>

        <FinhudasCTA awareness="solution" />

        <RelatedArticles articles={[
          { slug: 'atvirkstinis-pvm-pavezejams', title: 'Atvirkštinis PVM: mokestis, kurį dauguma pamiršta' },
          { slug: 'individualios-veiklos-islaidos-30-procentu-arba-faktines', title: '30% norma ar faktinės išlaidos 2026' },
          { slug: 'pavezejo-mokesciai-gpm-psd-vsd', title: 'Mokesčiai pavežėjui 2026: GPM, PSD, VSD ir PVM' }
        ]} />

        <motion.div className="mt-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          <ButtonLink variant="secondary" to="/blog">Grįžti į straipsnius</ButtonLink>
        </motion.div>
      </div>
    </>
  )
}

export default AccountantVsFinhudasPage
